import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Row, Typography, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import i18n from 'i18n';
import _invoke from 'lodash/invoke';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { usePrevious, useSocket } from 'hooks';
import {
  getConversationListRequest,
  getUnreadConversationsRequest,
  setConversationFilter,
  conversationSelectors,
  messageSelectors,
  getMessageListRequest,
  sendMessageRequest,
  updateMessage,
  incrementUnseenMessages,
  getConversationDetailRequest,
  seenConversationRequest,
  firstGetListMessageFalse,
  triggerModalTag,
  getFonehouseTagsRequest,
  sendingMessage,
  finishSendMessage,
  selectUnreadCount,
} from 'providers/MessengerProvider/slice';
import { getBillingListRequest } from 'providers/BillingProvider/slice';
import './styles.less';
import Helper from 'utils/helpers';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageInput from './MessageList/MessageInput';
import MessageList from './MessageList';
import ConversationHeader from './MessageList/ConversationHeader';
import ConversationList from './ConversationList';
import CreateTagModal from './CreateTagModal';
import SetTagModal from './SetTagModal';
import FilterTag from './FilterTag';

const Messenger = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const formRef = useRef();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = _get(currentUser, '_id');
  const currentUserRole = _get(currentUser, 'role');
  const { conversationId } = useParams();
  const conversationIds = useSelector(conversationSelectors.selectIds);
  const conversationEntities = useSelector(conversationSelectors.selectEntities);
  const conversationHasMore = useSelector((state) => state.messenger.conversations.hasMore);
  const conversationPage = useSelector((state) => state.messenger.conversations.page);
  const conversationFilter = useSelector((state) => state.messenger.conversationFilter);
  const isGettingList = useSelector((state) => state.messenger.isGettingList);

  // Use memoized selector to prevent unnecessary re-renders
  const unreadCount = useSelector(selectUnreadCount);
  const opponentUserId = _get(conversationEntities[conversationId], 'member[0]');
  const billingList = useSelector((state) => state.billingList.docs);
  const latestBill = billingList[0];

  const messageIds = useSelector(messageSelectors.selectIds);
  const messageEntities = useSelector(messageSelectors.selectEntities);
  const messageHasMore = useSelector((state) => state.messenger.messages.hasMore);
  const lastMessageTime = useSelector((state) => state.messenger.messages.lastMessageTime);
  const firstGetList = useSelector((state) => state.messenger.firstGetList);
  const isGettingMessageList = useSelector((state) => state.messenger.isGettingMessageList);
  const { isRemovingTag } = useSelector((state) => state.messenger);
  const firstConversationId = conversationIds[0];
  const prevFirstConversationId = usePrevious(firstConversationId);
  const { socket, isConnected } = useSocket();

  useEffect(() => {
    dispatch(getFonehouseTagsRequest());
  }, [dispatch]);

  // Define functions first before using them in useEffect
  const handleGetMessageList = useCallback(() => {
    dispatch(
      getMessageListRequest({
        limit: 15,
        time: lastMessageTime,
        conversationId,
      })
    );
  }, [conversationId, dispatch, lastMessageTime]);

  const handleGetConversationList = useCallback(() => {
    if (conversationFilter === 'unread') {
      dispatch(
        getUnreadConversationsRequest({
          limit: 30,
          page: conversationPage + 1,
          type: 'FONEHOUSE_TO_USER',
          role: 'fonehouse',
          unreadOnly: true,
        })
      );
    } else {
      dispatch(
        getConversationListRequest({
          limit: 30, // increase to 30 for better screen fit
          page: conversationPage + 1,
          type: 'FONEHOUSE_TO_USER',
          role: 'fonehouse',
        })
      );
    }
  }, [conversationPage, dispatch, conversationFilter]);

  const handleConversationCellClick = useCallback(
    (_conversationId) => {
      dispatch(firstGetListMessageFalse());

      navigate(`/fonehouse/messengers/${_conversationId}`, { replace: true });
    },
    [dispatch, navigate]
  );

  const handleFilterChange = useCallback(
    (key) => {
      dispatch(setConversationFilter(key));
      // Reset to first page when filter changes
      dispatch(
        key === 'unread'
          ? getUnreadConversationsRequest({
              limit: 30,
              page: 1,
              type: 'FONEHOUSE_TO_USER',
              role: 'fonehouse',
            })
          : getConversationListRequest({
              limit: 30,
              page: 1,
              type: 'FONEHOUSE_TO_USER',
              role: 'fonehouse',
            })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, conversationFilter]
  );

  const refreshConversationList = useCallback(() => {
    if (conversationFilter === 'unread') {
      dispatch(
        getUnreadConversationsRequest({
          limit: 30,
          page: 1,
          type: 'FONEHOUSE_TO_USER',
          role: 'fonehouse',
          unreadOnly: true,
        })
      );
    } else {
      dispatch(
        getConversationListRequest({
          limit: 30,
          page: 1,
          type: 'FONEHOUSE_TO_USER',
          role: 'fonehouse',
        })
      );
    }
  }, [dispatch, conversationFilter]);

  // Debounced refresh to prevent excessive API calls on rapid messages
  const debouncedRefresh = useMemo(
    () => _debounce(() => refreshConversationList(), 500, { leading: false, trailing: true }),
    [refreshConversationList]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedRefresh.cancel();
    };
  }, [debouncedRefresh]);

  // Socket: new_message handler
  useEffect(() => {
    console.log('[Socket] useEffect check:', { currentUserId, socket: !!socket, isConnected });

    if (currentUserId && socket && isConnected) {
      // Debug: Listen to ALL socket events
      socket.onAny((eventName, ...args) => {
        console.log('[Socket] ANY EVENT:', eventName, args);
      });

      const setupConnection = () => {
        console.log('[Socket] Setting up admin connection');
        console.log('[Socket] Socket ID:', socket.id);
        console.log('[Socket] Socket connected:', socket.connected);
        // Join admin room để nhận TẤT CẢ messages từ users
        socket.emit('unread-conversations', { room: 'admin-room' });
        // Subscribe conversation cụ thể
        socket.emit('subscribe', { room: conversationId });
        console.log('[Socket] Emitted unread-conversations and subscribe for room:', conversationId);
      };

      // Initial setup
      setupConnection();

      // Re-setup on reconnect
      socket.on('connect', setupConnection);

      const handleNewMessage = (data) => {
        console.log('[Socket] RAW new_message data:', JSON.stringify(data));
        console.log('[Socket] Current conversationId:', conversationId);

        const dataConversationId = _get(data, 'message.conversationId');
        const isFromAdmin = _get(data, 'message.isAdmin') === 1;

        console.log('[Socket] Message conversationId:', dataConversationId);
        console.log('[Socket] Match:', dataConversationId === conversationId);
        console.log('[Socket] isFromAdmin:', isFromAdmin);

        if (dataConversationId === conversationId) {
          // Current conversation - update message and finish sending
          console.log('[Socket] MATCH! Updating message in UI');
          dispatch(finishSendMessage());
          dispatch(updateMessage({ ...data }));
        } else if (!isFromAdmin) {
          // Different conversation & not from admin - immediately increment badge
          console.log('[Socket] Different conversation, incrementing badge');
          dispatch(incrementUnseenMessages({ conversationId: dataConversationId }));
        }
        // Debounced refresh to reduce API calls
        debouncedRefresh();
      };

      socket.on('new_message', handleNewMessage);
      return () => {
        socket.off('connect', setupConnection);
        socket.off('new_message', handleNewMessage);
      };
    }
  }, [conversationId, currentUserId, dispatch, debouncedRefresh, socket, isConnected]);

  // Socket: conversation-change handler
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const handleConversationChange = (data) => {
        console.log('[Socket] Received conversation-change:', data);
        dispatch(getConversationDetailRequest(data));
      };
      socket.on('conversation-change', handleConversationChange);
      return () => {
        socket.off('conversation-change', handleConversationChange);
      };
    }
  }, [currentUserId, dispatch, socket, isConnected]);

  useEffect(() => {
    if (!prevFirstConversationId && firstConversationId && !conversationId) {
      navigate(`/fonehouse/messengers/${firstConversationId}`, {
        replace: true,
      });
    }
  }, [conversationId, firstConversationId, navigate, prevFirstConversationId]);

  useEffect(() => {
    if (conversationFilter === 'unread') {
      dispatch(
        getUnreadConversationsRequest({
          limit: 30,
          page: 1,
          type: 'FONEHOUSE_TO_USER',
          role: 'fonehouse',
        })
      );
    } else {
      dispatch(
        getConversationListRequest({
          limit: 30, // increase to 30 for better screen fit
          page: 1,
          type: 'FONEHOUSE_TO_USER',
          role: 'fonehouse',
        })
      );
    }
  }, [dispatch, conversationFilter]);

  useEffect(() => {
    if (conversationId) {
      dispatch(seenConversationRequest({ conversationId }));
      dispatch(getMessageListRequest({ limit: 50, conversationId })); // reduce from 500 to 50
    }
  }, [conversationId, dispatch]);

  useEffect(() => {
    if (opponentUserId) {
      dispatch(getBillingListRequest({ userId: opponentUserId, limit: 1, page: 1 }));
    }
  }, [dispatch, opponentUserId]);

  // Removed: Periodic refresh not needed - socket provides real-time updates
  // Debounced refresh in new_message handler is sufficient

  const handleSendMessage = useCallback(
    async (values) => {
      try {
        dispatch(sendingMessage());
        formRef.current.resetForm();
        let attachments = [];

        // Handle regular file attachments (need uploading)
        if (!_isEmpty(values.attachments)) {
          const imageFiles = values.attachments.filter((file) => file.type === 'image');
          const videoFiles = values.attachments.filter((file) => file.type === 'video');
          const totalFiles = imageFiles.length + videoFiles.length;

          if (totalFiles > 0) {
            setUploading(true);
            setUploadProgress(0);

            const fileProgresses = {};

            // Track progress for each file
            const handleProgress = (fileIndex) => (progress) => {
              fileProgresses[fileIndex] = progress;
              const totalProgress = Object.values(fileProgresses).reduce((sum, p) => sum + p, 0);
              const avgProgress = Math.round(totalProgress / totalFiles);
              setUploadProgress(avgProgress);
            };

            // Upload videos (no progress tracking available, mark 100% on complete)
            const videoPromises = videoFiles.map((file, idx) =>
              Helper.uploadVideo(file).then((res) => {
                handleProgress(idx)(100);
                return res;
              })
            );

            // Upload images with progress
            const imagePromises = imageFiles.map((file, idx) =>
              Helper.uploadFile(file, handleProgress(videoFiles.length + idx))
            );

            const results = await Promise.all([...videoPromises, ...imagePromises]);
            const uploadedAttachments = results.filter(Boolean).map((res) => ({
              type: res.attachmentType,
              data: res.attachmentType === 'image' ? { imageUrl: res.url } : { videoUrl: res.url },
            }));
            attachments = attachments.concat(uploadedAttachments);
            setUploading(false);
            setUploadProgress(0);
          }
        }

        // Handle template images (already uploaded, just need URLs)
        if (!_isEmpty(values.templateImages)) {
          const templateAttachments = values.templateImages.map((img) => ({
            type: 'image',
            data: { imageUrl: img.url },
          }));
          attachments = attachments.concat(templateAttachments);
        }

        const trimmedMessage = _invoke(values.message, 'trim');
        if (trimmedMessage || !_isEmpty(attachments)) {
          dispatch(
            sendMessageRequest({
              type: 'text',
              text: trimmedMessage || null,
              conversationId,
              attachments,
            })
          ).then((result) => {
            socket?.emit('new_message_admin', {
              room: conversationId,
              messageId: result._id,
            });
          });
        }
      } catch (error) {
        setUploading(false);
        setUploadProgress(0);
        dispatch(finishSendMessage());
      }
    },
    [conversationId, dispatch, socket]
  );

  const handleShowModalTag = useCallback(() => {
    dispatch(
      triggerModalTag({
        visible: true,
        conversationId,
        conversationEntities,
      })
    );
  }, [dispatch, conversationEntities, conversationId]);

  return (
    <div className="messenger-page">
      {isRemovingTag && <LoadingIndicator />}
      <Row className="bill-list-header" justify={'space-between'} style={{ alignItems: 'center' }}>
        <Typography.Title level={4}>{i18n.t('messenger.pageTitle')}</Typography.Title>

        <Space>
          <Button size="large" onClick={() => navigate('/fonehouse/message-templates')}>
            Quản lý Template
          </Button>
          <Button size="large" type="primary" onClick={handleShowModalTag}>
            Tạo nhãn dán
          </Button>
        </Space>
      </Row>
      <Row className="color-legend" style={{ marginBottom: 16, padding: '0 16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            fontSize: 12,
            color: '#666',
          }}
        >
          <span style={{ fontWeight: 500 }}>Chú thích màu sắc:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#bfffe2',
                borderRadius: 4,
                border: '1px solid #ddd',
              }}
            ></div>
            <span>Tin nhắn chưa đọc</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#ffebee',
                borderRadius: 4,
                border: '1px solid #ddd',
              }}
            ></div>
            <span>Đã đọc nhưng chưa trả lời</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: 'transparent',
                borderRadius: 4,
                border: '1px solid #ddd',
              }}
            ></div>
            <span>Đã đọc và đã trả lời</span>
          </div>
        </div>
      </Row>
      <FilterTag />
      <div className="message-page-content">
        <div className="conversation-list-section">
          {/* Tabs section - full width */}
          <div
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fafafa',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '0',
                width: '100%',
                marginBottom: '8px',
              }}
            >
              <button
                onClick={() => handleFilterChange('all')}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  border: '1px solid #d9d9d9',
                  borderRight: 'none',
                  borderRadius: '6px 0 0 6px',
                  background: conversationFilter === 'all' ? '#1890ff' : 'white',
                  color: conversationFilter === 'all' ? 'white' : '#333',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: conversationFilter === 'all' ? '500' : '400',
                  transition: 'all 0.2s',
                  outline: 'none',
                }}
              >
                Tất cả
              </button>
              <button
                onClick={() => handleFilterChange('unread')}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '0 6px 6px 0',
                  background: conversationFilter === 'unread' ? '#1890ff' : 'white',
                  color: conversationFilter === 'unread' ? 'white' : '#333',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: conversationFilter === 'unread' ? '500' : '400',
                  transition: 'all 0.2s',
                  outline: 'none',
                  position: 'relative',
                }}
              >
                Chưa đọc
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      background: '#ff4d4f',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      border: '2px solid white',
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>

            {/* Refresh button below tabs */}
            <div style={{ textAlign: 'right' }}>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={refreshConversationList}
                title="Làm mới danh sách"
                style={{ padding: '4px 8px' }}
                loading={isGettingList}
              >
                Làm mới
              </Button>
            </div>
          </div>
          <ConversationList
            activeConversationId={conversationId}
            onConversationCellClick={handleConversationCellClick}
            conversationIds={conversationIds}
            hasMore={conversationHasMore}
            getConversationList={handleGetConversationList}
            conversationEntities={conversationEntities}
            currentUserId={currentUserId}
            isGettingList={isGettingList}
          />
        </div>
        <div className="conversation-container">
          <ConversationHeader conversationDetail={conversationEntities[conversationId]} latestBill={latestBill} />
          <MessageList
            firstGetList={firstGetList}
            isGettingMessageList={isGettingMessageList}
            hasMore={messageHasMore}
            getMessageList={handleGetMessageList}
            messageIds={messageIds}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            messageEntities={messageEntities}
          />
          <MessageInput
            conversationId={conversationId}
            ref={formRef}
            uploading={uploading}
            uploadProgress={uploadProgress}
            onClick={handleSendMessage}
          />
        </div>
      </div>
      <CreateTagModal />
      <SetTagModal />
    </div>
  );
};

export default Messenger;
